/**
 * URL hygiene for analytics. GA4 attaches the browser's current URL to EVERY event it sends, not
 * just page views, so keeping secrets out of analytics means keeping the container away from URLs
 * that carry them; trimming the page view alone is not enough. Mirrors src/analytics/location.ts
 * in lpa.ui, so both apps report the same shape into the shared container.
 *
 *  - /confirmPassword and /setPassword carry the customer's email address in the query string
 *    (the set-password page needs it on reload), and /recording-token/<token> carries a secret in
 *    the path. The container is NOT LOADED while the address bar shows one of these, and no page
 *    view is reported for them. It loads on the first safe route instead (analyticsService.ts).
 *  - Marketing parameters (utm_*, click ids) are what GA4 attributes campaigns from, so page views
 *    keep exactly those and drop everything else.
 */

// Compared case-insensitively: the router matches routes that way too.
const SENSITIVE_PATH_PREFIXES = ['/confirmpassword', '/setpassword', '/recording-token'];
const SENSITIVE_PARAMS = new Set(['token', 'userid', 'email', 'username', 'code', 'password']);
const MARKETING_PARAM = /^(utm_[a-z0-9_]+|gclid|gbraid|wbraid|dclid|fbclid|msclkid|ttclid|li_fat_id)$/i;

// True while the address bar holds something analytics must never see.
export const isSensitiveLocation = (pathname: string, search: string): boolean => {
  const path = pathname.toLowerCase();
  if (SENSITIVE_PATH_PREFIXES.some((prefix) => path.startsWith(prefix))) {
    return true;
  }

  let sensitive = false;
  new URLSearchParams(search).forEach((_value, key) => {
    if (SENSITIVE_PARAMS.has(key.toLowerCase())) {
      sensitive = true;
    }
  });
  return sensitive;
};

// The marketing parameters of `search`, as a query string without the leading "?".
export const marketingQuery = (search: string): string => {
  const kept = new URLSearchParams();
  new URLSearchParams(search).forEach((value, key) => {
    if (MARKETING_PARAM.test(key)) {
      kept.append(key, value);
    }
  });
  return kept.toString();
};

// origin + pathname + the marketing parameters only: what a page view reports as its URL.
// `landingQuery` adds the campaign the visit ARRIVED with. The affiliate landing and recording-token
// pages navigate on as soon as they mount, which drops the query string before the first page view
// is reported, and GA4 attributes the whole session from that first page view.
export const safePageLocation = (origin: string, pathname: string, search: string, landingQuery = ''): string => {
  const merged = new URLSearchParams(landingQuery);
  new URLSearchParams(marketingQuery(search)).forEach((value, key) => {
    merged.set(key, value);
  });
  const query = merged.toString();
  return `${origin}${pathname}${query ? `?${query}` : ''}`;
};
